import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { InfrastructureTableContext } from './InfrastructureTableContext';
import { useSelectorStyles } from '../../../form/ListSelectorStyles';

/**
 * SelectorContainer compound component for InfrastructureTable.
 *
 * Wraps child components in a styled container div, following the pattern
 * from the original TableContent component. Used to apply consistent styling
 * for tables that need the container wrapper.
 *
 * **Usage:**
 * ```jsx
 * <InfrastructureTable {...props}>
 *   <InfrastructureTable.SelectorContainer>
 *     <InfrastructureTable.Content />
 *   </InfrastructureTable.SelectorContainer>
 * </InfrastructureTable>
 * ```
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to wrap in container
 */
const SelectorContainer = ({ children }) => {
    const context = useContext(InfrastructureTableContext);

    if (!context) {
        throw new Error(
            'SelectorContainer must be used within InfrastructureTable. ' +
                'Wrap your SelectorContainer component in <InfrastructureTable>...</InfrastructureTable>'
        );
    }

    // Use custom styles from context if provided, otherwise use default
    const defaultStyles = useSelectorStyles();
    const styles = context.useStyles?.() || defaultStyles;

    return <div className={styles.selectorContainer}>{children}</div>;
};

SelectorContainer.propTypes = {
    children: PropTypes.node.isRequired,
};

// Memoize to prevent re-renders when parent context changes but props are stable
export default React.memo(SelectorContainer);
